/**
 * @file cart-look.ts
 * @description Панель «Вид корзины» без разметки (phase19.16, партия SB120,
 * этап 4; uiux-cart §4–§5, db-design-sb120-stage4 §1–§2): раздел корзины из
 * темы, сводки групп, какие предупреждения `cartLookWarnings` к какой
 * группе и полю относятся, фон под текстом для контраста.
 *
 * Группы и пути — реестром (OCP): новое поле корзины — путь в
 * `CART_GROUP_PATHS`, и его предупреждение само попадёт в «!» группы.
 */

import {
  cartEffectiveFill,
  DEFAULT_SITE_CART,
  resolveSurfaceFill,
  SITE_CART_AFTER_ADD_LABELS,
  type SiteAutoTextColor,
  type SiteCart,
  type SiteCartWarning,
  type SiteCartWarningCode,
  type SiteSurfaceFill,
  type SiteTheme,
} from "@chaiberi/shared";

import { TEXT_COLOR_ROLE_LABELS } from "../site-fields/text-color-labels";
import { pluralRu } from "../plural";
import { surfaceFillLabel } from "./surface-fill-labels";

/**
 * Раздел корзины из темы. Сервер дописывает умолчание при чтении
 * (`readSiteTheme`); старый сервер во время выкладки раздела не знает —
 * тогда умолчание «как сейчас», и запись уходит разделом целиком.
 */
export function cartOf(theme: SiteTheme): SiteCart {
  const cart: SiteCart | undefined = (theme as Partial<Pick<SiteTheme, "cart">>).cart;
  return cart ?? DEFAULT_SITE_CART;
}

/** Группы панели (SB122): ключ — `?group=` и память свёрнутости. */
export type CartGroupId = "mode" | "size" | "overlay" | "colors" | "rows" | "after" | "platform";

/** Поля группы — пути `cartLookWarnings` («!» у группы). */
export const CART_GROUP_PATHS: Readonly<Record<CartGroupId, readonly string[]>> = {
  mode: ["cart.mode", "cart.drawer.side"],
  size: ["cart.drawer.widthPhone", "cart.drawer.widthWide", "cart.popover.width", "cart.popover.rows"],
  overlay: ["cart.drawer.dim", "cart.popover.dim", "cart.overlayColor"],
  colors: ["cart.fill", "cart.text", "cart.summaryFill", "cart.totalColor"],
  rows: ["cart.photo", "cart.radius"],
  after: ["cart.afterAdd"],
  platform: [],
};

/**
 * Предупреждения о контрасте показывает само поле цвета (число и «Взять
 * читаемый»), под полем их не повторяем; в «!» группы они идут.
 */
const FIELD_NOTE_CODES: ReadonlySet<SiteCartWarningCode> = new Set(["cart_text_contrast", "cart_total_contrast"]);

/** Предупреждения под полем с этим путём. */
export function warningsAt(warnings: readonly SiteCartWarning[], path: string): SiteCartWarning[] {
  return warnings.filter((w) => w.path === path && !FIELD_NOTE_CODES.has(w.code));
}

/** «!» у группы: есть предупреждение по любому её полю. */
export function groupHasWarning(warnings: readonly SiteCartWarning[], group: CartGroupId): boolean {
  const paths = CART_GROUP_PATHS[group];
  return warnings.some((w) => paths.includes(w.path));
}

/** Одинаковы ли значения (для «↺» только у изменённого). */
export function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Строка вида «окно» (uiux-cart §5; решение оркестратора по db-design §1.3: окно на телефоне — панель справа). */
export const CART_POPOVER_PHONE_NOTE = "На телефоне окно открывается панелью справа шириной «на телефоне».";

/** Строка «Только просмотр» без права THEME (uiux-cart §7). */
export const CART_READONLY_TEXT = "Только просмотр. Меняет владелец магазина или сотрудник с правом настроек магазина.";

/** Подсказка «Как шапка» при прозрачной шапке (uiux-cart §7). */
export const CART_TRANSPARENT_HEADER_TEXT = "Шапка прозрачная — корзина возьмёт её заливку после прокрутки.";

// ── Сводки свёрнутых групп (uiux-cart §5) ──

export function modeSummary(cart: SiteCart): string {
  return cart.mode === "popover" ? "окно из шапки" : cart.drawer.side === "left" ? "панель слева" : "панель справа";
}

export function sizeSummary(cart: SiteCart): string {
  if (cart.mode === "drawer") return `${cart.drawer.widthPhone} / ${cart.drawer.widthWide} px`;
  const rows = cart.popover.rows;
  return `окно ${cart.popover.width} px · ${rows} ${pluralRu(rows, ["строка", "строки", "строк"])}`;
}

/** Затемнение того вида, что выбран. */
export function cartDim(cart: SiteCart): number {
  return cart.mode === "drawer" ? cart.drawer.dim : cart.popover.dim;
}

export function overlaySummary(cart: SiteCart): string {
  const dim = cartDim(cart);
  return dim === 0 ? "без затемнения" : `${dim} %`;
}

/** Подпись цвета текста: «авто», «акцент», «свой цвет». */
export function textColorLabel(color: SiteAutoTextColor): string {
  if (color.kind === "auto") return "авто";
  return color.kind === "role" ? TEXT_COLOR_ROLE_LABELS[color.role].toLowerCase() : "свой цвет";
}

/** Подпись заливки корзины: `null` — «как шапка». */
export function cartFillLabel(fill: SiteSurfaceFill | null): string {
  return fill === null ? "как шапка" : surfaceFillLabel(fill).toLowerCase();
}

export function colorsSummary(cart: SiteCart): string {
  return `${cartFillLabel(cart.fill)} · ${textColorLabel(cart.text)}`;
}

export function rowsSummary(cart: SiteCart): string {
  return `фото ${cart.photo} · ${cart.radius === null ? "как у сайта" : `${cart.radius} px`}`;
}

export function afterSummary(cart: SiteCart): string {
  return SITE_CART_AFTER_ADD_LABELS[cart.afterAdd].toLowerCase();
}

// ── Фон под текстом — для контраста в поле цвета ──

/** Фон корзины, на котором стоит текст строк (своя заливка или шапки). */
export function cartTextBg(cart: SiteCart, theme: SiteTheme): string {
  return resolveSurfaceFill(cartEffectiveFill(cart, theme), theme).background;
}

/** Фон «Итого», на котором стоит сумма. */
export function cartSummaryBg(cart: SiteCart, theme: SiteTheme): string {
  return resolveSurfaceFill(cart.summaryFill, theme).background;
}
